<p-toast></p-toast>
<p-progressBar mode="indeterminate" *ngIf="isLoading"></p-progressBar>

<div class="p-fluid">
  <app-warehousing-page-header activeSection="transfers" title="Transfers"
    subtitle="Move whole stock units between warehouses with outbound scan control and destination receipt.">
    <div actions class="flex gap-2">
      <p-button label="New Transfer" icon="pi pi-plus" routerLink="/warehousing/transfers/add"></p-button>
      <p-button label="Refresh" icon="pi pi-refresh" severity="secondary" [outlined]="true" (click)="loadTransfers()"></p-button>
    </div>
  </app-warehousing-page-header>

  <app-warehousing-search-panel title="Search Transfers" [resultCount]="transfers.length" [totalCount]="transfers.length" (search)="applySearch()" (clear)="clearSearch()">
    <div [formGroup]="searchForm" class="formgrid grid">
      <div class="field col-12 md:col-3">
        <p-inputGroup><p-inputGroupAddon><i class="pi pi-search"></i></p-inputGroupAddon><p-iftaLabel><input id="transferKeyword" pInputText formControlName="keyword"><label for="transferKeyword">Keyword</label></p-iftaLabel></p-inputGroup>
      </div>
      <div class="field col-12 md:col-3">
        <p-inputGroup><p-inputGroupAddon><i class="pi pi-building"></i></p-inputGroupAddon><p-iftaLabel><p-select id="transferClient" [options]="clients" optionLabel="companyName" optionValue="id" formControlName="crmClientId" [filter]="true" [showClear]="true" styleClass="w-full"></p-select><label for="transferClient">Client</label></p-iftaLabel></p-inputGroup>
      </div>
      <div class="field col-12 md:col-2">
        <p-inputGroup><p-inputGroupAddon><i class="pi pi-warehouse"></i></p-inputGroupAddon><p-iftaLabel><p-select id="sourceWarehouse" [options]="warehouses" optionLabel="name" optionValue="id" formControlName="sourceWarehouseId" [filter]="true" [showClear]="true" styleClass="w-full"></p-select><label for="sourceWarehouse">Source</label></p-iftaLabel></p-inputGroup>
      </div>
      <div class="field col-12 md:col-2">
        <p-inputGroup><p-inputGroupAddon><i class="pi pi-map-marker"></i></p-inputGroupAddon><p-iftaLabel><p-select id="destinationWarehouse" [options]="warehouses" optionLabel="name" optionValue="id" formControlName="destinationWarehouseId" [filter]="true" [showClear]="true" styleClass="w-full"></p-select><label for="destinationWarehouse">Destination</label></p-iftaLabel></p-inputGroup>
      </div>
      <div class="field col-12 md:col-2">
        <p-inputGroup><p-inputGroupAddon><i class="pi pi-flag"></i></p-inputGroupAddon><p-iftaLabel><p-select id="transferStatus" [options]="statusOptions" optionLabel="label" optionValue="value" formControlName="status" [showClear]="true" styleClass="w-full"></p-select><label for="transferStatus">Status</label></p-iftaLabel></p-inputGroup>
      </div>
      <div class="field col-12 md:col-6">
        <div class="grid">
          <div class="field col-6 mb-0"><p-inputGroup><p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon><p-iftaLabel><input id="transferScheduledFrom" pInputText type="date" formControlName="scheduledFrom"><label for="transferScheduledFrom">From</label></p-iftaLabel></p-inputGroup></div>
          <div class="field col-6 mb-0"><p-inputGroup><p-inputGroupAddon><i class="pi pi-calendar-times"></i></p-inputGroupAddon><p-iftaLabel><input id="transferScheduledTo" pInputText type="date" formControlName="scheduledTo"><label for="transferScheduledTo">To</label></p-iftaLabel></p-inputGroup></div>
        </div>
      </div>
    </div>
  </app-warehousing-search-panel>

  <section class="warehousing-table-card">
    <div class="warehousing-table-header">
      <div>
        <h2>Warehouse Transfer Directory</h2>
        <p>Track reserved units, outbound scan progress, in-transit stock, and destination receipts.</p>
      </div>
      <span class="warehousing-result-chip">{{ transfers.length }} records</span>
    </div>
    <p-table [value]="transfers" [paginator]="true" [rows]="12" dataKey="id" styleClass="p-datatable-sm p-datatable-gridlines warehousing-data-table" [showCurrentPageReport]="true" currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries" [rowsPerPageOptions]="[12, 25, 50]" [tableStyle]="{ 'min-width': '60rem' }">
      <ng-template pTemplate="header"><tr><th>Transfer Profile</th><th>Warehouse Flow</th><th>Status / Quantities</th><th>Audit</th><th class="text-center">Controls</th></tr></ng-template>
      <ng-template pTemplate="body" let-transfer>
        <tr>
          <td>
            <div class="stack-cell">
              <span class="warehousing-code-pill">{{ transfer.transferNumber || '-' }}</span>
              <span class="cell-title">{{ clientName(transfer.crmClientId) }}</span>
              <span class="cell-meta"><i class="pi pi-calendar"></i> {{ transfer.scheduledDate || 'Not scheduled' }}</span>
              <span class="cell-meta"><i class="pi pi-bookmark"></i> {{ transfer.referenceNumber || 'No reference' }}</span>
              @if (transfer.shipmentNumber) {
                <span class="cell-meta"><i class="pi pi-truck"></i> {{ transfer.shipmentNumber }}</span>
              }
            </div>
          </td>
          <td>
            <div class="stack-cell">
              <span class="cell-title">{{ warehouseName(transfer.sourceWarehouseId) }} -> {{ warehouseName(transfer.destinationWarehouseId) }}</span>
              <span class="cell-meta"><i class="pi pi-list"></i> {{ transfer.lines?.length || 0 }} lines</span>
              @if (transfer.transportReleaseNumber) {
                <span class="cell-meta"><i class="pi pi-link"></i> {{ transfer.transportReleaseNumber }}</span>
              }
              @if (transfer.deliveryNumber) {
                <span class="cell-meta"><i class="pi pi-map-marker"></i> {{ transfer.deliveryNumber }}</span>
              }
              <span class="cell-meta"><i class="pi pi-calendar-plus"></i> Dispatched {{ transfer.dispatchDate || '-' }}</span>
              <span class="cell-meta"><i class="pi pi-calendar-check"></i> Received {{ transfer.receiptDate || '-' }}</span>
            </div>
          </td>
          <td>
            <div class="stack-cell">
              <p-tag [value]="transfer.status" [severity]="statusSeverity(transfer.status)"></p-tag>
              <span class="cell-meta"><i class="pi pi-chart-bar"></i> {{ quantitiesSummary(transfer) }}</span>
            </div>
          </td>
          <td><app-audit-directory-cell [stamp]="transfer"></app-audit-directory-cell></td>
          <td class="text-center">
            <div class="warehousing-row-actions">
              <p-button icon="pi pi-eye" [text]="true" [routerLink]="['/warehousing/transfers/view', transfer.id]"></p-button>
              @if (canReserve(transfer)) { <p-button label="Reserve" size="small" severity="warn" [outlined]="true" [loading]="isSubmitting" [disabled]="isSubmitting" (click)="reserveTransfer(transfer)"></p-button> }
              @if (canContinue(transfer)) { <p-button label="Desktop work" size="small" severity="info" [outlined]="true" [routerLink]="['/warehousing/transfers/view', transfer.id]"></p-button> }
              @if (canCancel(transfer)) { <p-button label="Cancel" size="small" severity="danger" [text]="true" [loading]="isSubmitting" [disabled]="isSubmitting" (click)="cancelTransfer(transfer)"></p-button> }
            </div>
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage"><tr><td colspan="5"><div class="warehousing-empty-state"><i class="pi pi-search"></i><strong>No transfers found</strong><span>No matching transfer records.</span></div></td></tr></ng-template>
    </p-table>
  </section>
</div>
